/**
 * FormMultiSelect — sélection multiple via checkbox-list, lié RHF (S2.5d).
 *
 *   <FormMultiSelect
 *     name="format_ids"
 *     label="Formats inclus"
 *     options={formatOptions}
 *     required
 *     helper="Pour un set digital, sélectionne plusieurs tailles."
 *   />
 *
 * Pourquoi pas `<select multiple>` natif ? Dispatch limité côté Etsy/digital
 * download (l'utilisatrice doit voir d'un coup tous les formats sélectionnés
 * sans cmd-click natif), pas accessible au clavier sur certains browsers,
 * et le rendu natif est inconsistant (gros bloc gris peu engageant). On
 * rend une liste de checkboxes — accessible, prévisible, stylable avec
 * les tokens DS.
 *
 * RHF : la valeur stockée est `string[]` (les `value` des options). Le
 * schéma Zod côté page consommatrice coerce en `int[]` si besoin (cf.
 * `formatIdsFromStringsArray` dans `schemas/works.ts`).
 */
import { useFormContext, useWatch, type FieldPath, type FieldValues } from 'react-hook-form'

import Checkbox from '@/components/ui/Checkbox'
import Field from '@/components/ui/Field'
import { getNestedError } from '@/utils/formErrors'

import './FormMultiSelect.css'

interface FormMultiSelectOption {
  value: string | number
  label: string
}

interface FormMultiSelectProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  label: string
  options: FormMultiSelectOption[]
  required?: boolean
  helper?: string
  disabled?: boolean
  hideLabel?: boolean
  /** Texte affiché quand la liste d'options est vide. */
  emptyMessage?: string
  /**
   * Nom de l'entité au singulier ("format", "type", …) pour le compteur
   * "N X sélectionné(s)". Défaut "format" pour rétro-compat (S2.5d).
   */
  entityNoun?: string
}

export function FormMultiSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  options,
  required,
  helper,
  disabled,
  hideLabel,
  emptyMessage,
  entityNoun = 'format',
}: FormMultiSelectProps<TFieldValues>) {
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<TFieldValues>()

  const errorMessage = getNestedError(errors as Record<string, unknown>, name)

  // Watch est nécessaire pour calculer le compteur "N sélectionné(s)" en
  // dynamique. Pas la valeur RHF directe parce que `register` rend les
  // checkboxes non-contrôlés — on s'aligne sur ce pattern.
  const watched = useWatch({ control, name }) as unknown
  const selectedCount = Array.isArray(watched) ? watched.length : 0

  return (
    <Field
      label={label}
      required={required}
      helper={helper}
      error={errorMessage}
      hideLabel={hideLabel}
    >
      {(controlProps) => (
        <div
          className="form-multi-select"
          role="group"
          aria-labelledby={controlProps.id}
          aria-describedby={controlProps['aria-describedby']}
          aria-invalid={controlProps['aria-invalid']}
        >
          {options.length === 0 ? (
            <p className="form-multi-select-empty">{emptyMessage ?? 'Aucune option disponible.'}</p>
          ) : (
            <>
              <ul className="form-multi-select-list">
                {options.map((opt) => (
                  <li key={opt.value} className="form-multi-select-item">
                    <label className="form-multi-select-label">
                      <Checkbox value={String(opt.value)} disabled={disabled} {...register(name)} />
                      <span>{opt.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="form-multi-select-count" aria-live="polite">
                {selectedCount === 0
                  ? `Aucun ${entityNoun} sélectionné`
                  : selectedCount === 1
                    ? `1 ${entityNoun} sélectionné`
                    : `${selectedCount} ${entityNoun}s sélectionnés`}
              </p>
            </>
          )}
        </div>
      )}
    </Field>
  )
}

export default FormMultiSelect
